import React from 'react';
import { Form, Field, FormSpy } from 'react-final-form';
import { useStore } from 'effector-react';
import { Flex, Box, Image } from 'rebass';
import styled from 'styled-components';

import { Trans, t } from '@lingui/macro';
import { Button, Typography, Progress, Avatar } from '@oca/ui';
import {
  SelectWrapper,
  TextFieldWrapper,
  GroupSelectWrapper,
  DayPickerInputWrapper,
} from '@lib/final-form-oca-ui';
import { Row } from '@lib/grid';
import { i18n } from '@lib/i18n';
import { AvatarUploader } from '@features/common/organisms';
import {
  $employeeList,
  EmployeeOptionLabel,
  filterEmployeeOption,
} from '@features/common';

import { FORM_ERROR } from 'final-form';
import { getFullName } from '@lib/help-fns';
import dayjs from 'dayjs';
import {
  projectCreateRequesting,
  $employeeByDepartmentOptions,
} from '../model/project-flow';

// eslint-disable-next-line react/prop-types
export const FormBasic = React.memo(({ setIsOpen }) => {
  const employeeList = useStore($employeeList);
  const employeeByDepartmentList = useStore($employeeByDepartmentOptions);

  let selectedProjectLead = -1;

  return (
    <Wrapper>
      {employeeList && employeeList.length ? (
        <Form
          validate={validate}
          initialValues={{
            attendeesSelect: [],
            permissions: [],
            departments: [],
            projectLead: null,
          }}
          onSubmit={data => handleFormSubmit(data, setIsOpen)}
          render={({
            handleSubmit,
            submitError,
            submitting,
            values: formValues,
          }) => {
            const isFormReady =
              !!formValues.name &&
              !!formValues.description &&
              !!formValues.projectLead &&
              !!formValues.attendeesSelect &&
              formValues.attendeesSelect.length > 0;

            return (
              <Box style={{ minWidth: 450, maxWidth: 450 }}>
                <form onSubmit={handleSubmit} noValidate>
                  <Field
                    name="name"
                    label={<Trans>Project name</Trans>}
                    placeholder={i18n._(t`Name your project`)}
                    spaceAfter
                    helperText=" "
                    component={TextFieldWrapper}
                    required
                  />
                  <Field
                    name="description"
                    label={<Trans>Description</Trans>}
                    placeholder={i18n._(t`Describe your project`)}
                    spaceAfter
                    helperText=" "
                    component={TextFieldWrapper}
                    required
                  />
                  <Field
                    name="projectLead"
                    label={<Trans>Project Lead</Trans>}
                    component={SelectWrapper}
                    options={employeeList}
                    parse={val => val && val.id}
                    format={val =>
                      employeeList.find(employee => employee.id === val)
                    }
                    getOptionValue={option => option.id}
                    getOptionLabel={option => (
                      <EmployeeOptionLabel employee={option} />
                    )}
                    filterOption={filterEmployeeOption}
                    getOptionAvatar={option => option.avatar}
                    required
                    spaceAfter
                    helperText=" "
                    fullWidth
                  />
                  <Field
                    name="attendeesSelect"
                    parse={items => (items ? items.map(({ id }) => id) : [])}
                    format={items =>
                      items
                        ? employeeList.filter(({ id }) => items.includes(id))
                        : []
                    }
                    label={<Trans>Attendees</Trans>}
                    placeholder={i18n._(t`Select attendees...`)}
                    options={employeeByDepartmentList}
                    isOptionDisabled={item => item.id === selectedProjectLead}
                    getOptionLabel={item => (
                      <EmployeeOptionLabel employee={item} />
                    )}
                    filterOption={({ data }, str) => {
                      const fullName = getFullName(data, { middleKey: null });

                      return fullName
                        ? fullName.toLowerCase().includes(str)
                        : false;
                    }}
                    helperText=" "
                    isDisabled={!formValues.projectLead}
                    component={GroupSelectWrapper}
                    spaceAfter
                    required
                  />
                  <FormSpy>
                    {({ values, form }) => {
                      const { change } = form;
                      const {
                        attendeesSelect,
                        permissions,
                        projectLead,
                      } = values;

                      if (selectedProjectLead !== projectLead) {
                        selectedProjectLead = projectLead;
                        const nextAttendeesSelect = attendeesSelect.filter(
                          attendee => attendee !== projectLead,
                        );

                        change('attendeesSelect', nextAttendeesSelect);
                      }

                      const attendeesAccessCandidates = employeeList.filter(
                        ({ id }) => attendeesSelect.includes(id),
                      );
                      const nextPermissions = permissions.filter(id =>
                        attendeesSelect.includes(id),
                      );

                      if (permissions.length !== nextPermissions.length) {
                        change('permissions', nextPermissions);
                      }

                      return (
                        <Field
                          name="permissions"
                          label={<Trans>Manage access</Trans>}
                          component={SelectWrapper}
                          placeholder={i18n._(t`Select access...`)}
                          options={attendeesAccessCandidates}
                          getOptionValue={option => option.id}
                          getOptionLabel={option => (
                            <EmployeeOptionLabel employee={option} />
                          )}
                          filterOption={filterEmployeeOption}
                          getOptionAvatar={option => option.avatar}
                          parse={val => val && val.map(({ id }) => id)}
                          format={val =>
                            val
                              ? employeeList.filter(({ id }) =>
                                  val.includes(id),
                                )
                              : []
                          }
                          isDisabled={formValues.attendeesSelect.length < 1}
                          isMulti
                          required
                          spaceAfter
                          helperText=" "
                          fullWidth
                        />
                      );
                    }}
                  </FormSpy>

                  <Row gap={12} style={{ gap: 12 }}>
                    <Box flex={1}>
                      <Field
                        name="start_date"
                        placeholder={i18n._(t`Select start date`)}
                        label={<Trans>Start date</Trans>}
                        helperText=" "
                        component={DayPickerInputWrapper}
                        options={{
                          enableTime: false,
                        }}
                      />
                    </Box>
                    <Box flex={1}>
                      <Field
                        name="end_date"
                        placeholder={i18n._(t`Select end date`)}
                        label={<Trans>End date</Trans>}
                        helperText=" "
                        component={DayPickerInputWrapper}
                        options={{
                          enableTime: false,
                          minDate: formValues.start_date
                            ? new Date(formValues.start_date)
                            : undefined,
                        }}
                      />
                    </Box>
                  </Row>

                  <Flex flexDirection="column">
                    <Box mb={10}>
                      <Typography fontSize={14}>
                        <Trans>Project Image</Trans>
                      </Typography>
                      {!isFormReady && (
                        <Typography fontSize={12} color="#8C8C8C">
                          <Trans>
                            Fill required fields first to upload image
                          </Trans>
                        </Typography>
                      )}
                    </Box>

                    <Field
                      name="avatar"
                      format={val => (val ? val.fileUrl || val : '')}
                      render={({ input }) => {
                        const previewSrc =
                          // eslint-disable-next-line no-nested-ternary
                          typeof input.value === 'string'
                            ? input.value
                            : input.value && input.value.fileUrl
                            ? input.value.fileUrl
                            : '';

                        return (
                          <Row
                            flexDirection="column"
                            alignItems="stretch"
                            mb={3}
                          >
                            <Box
                              style={{
                                opacity: isFormReady ? 1 : 0.5,
                                pointerEvents: isFormReady ? 'auto' : 'none',
                                cursor: isFormReady ? 'pointer' : 'not-allowed',
                                transition: '0.2s ease',
                                width: '100%',
                              }}
                            >
                              <AvatarUploader
                                size={96}
                                src={previewSrc}
                                onUploaded={file => {
                                  if (!isFormReady) return;
                                  input.onChange(file);
                                }}
                              />
                            </Box>

                            {previewSrc ? (
                              <Avatar
                                mt={12}
                                size="100%"
                                borderRadius={8}
                                src={previewSrc}
                              />
                            ) : null}
                          </Row>
                        );
                      }}
                    />
                  </Flex>

                  <Box mt={3}>
                    {submitError && (
                      <Box p={2}>
                        <Typography variant="caption" color="error">
                          {submitError}
                        </Typography>
                      </Box>
                    )}
                    <Button
                      style={{ cursor: 'pointer' }}
                      disabled={submitting}
                      variant="primary"
                      type="submit"
                      fullWidth
                    >
                      <Trans>Create</Trans>
                    </Button>
                  </Box>
                </form>
              </Box>
            );
          }}
        />
      ) : (
        <Progress />
      )}
    </Wrapper>
  );
});

async function handleFormSubmit(
  { avatar, attendeesSelect, permissions, projectLead, ...data },
  setIsOpen,
) {
  const attendees = attendeesSelect.map(id => {
    return {
      employee: id,
      tasksPerm: permissions.includes(id),
    };
  });

  const payload = {
    ...data,
    projectLead,
    attendees: attendees.concat([{ employee: projectLead, taskPerm: true }]),
    start_date: data.start_date
      ? dayjs(data.start_date).format('YYYY-MM-DD')
      : null,
    end_date: data.end_date ? dayjs(data.end_date).format('YYYY-MM-DD') : null,
  };

  if (avatar && avatar.file) {
    payload.avatar = avatar.file;
  }

  const error = await new Promise(resolve =>
    projectCreateRequesting(payload)
      .then(() => {
        setIsOpen(false);
      })
      .catch(resolve),
  );

  if (error && error.message) {
    return { [FORM_ERROR]: error.message };
  }

  return error ? error.data : error;
}

function validate(values) {
  const errors = {};
  if (!values.name) {
    errors.name = <Trans>Please enter name</Trans>;
  }
  if (!values.description) {
    errors.description = <Trans>Please enter description</Trans>;
  }
  if (!values.projectLead) {
    errors.projectLead = <Trans>Please select project lead</Trans>;
  }

  if (!values.attendeesSelect || values.attendeesSelect.length === 0) {
    errors.attendeesSelect = <Trans>Please select attendees</Trans>;
  }

  if (
    values.start_date &&
    values.end_date &&
    dayjs(values.end_date).isBefore(dayjs(values.start_date), 'day')
  ) {
    errors.end_date = <Trans>End date cannot be earlier than start date</Trans>;
  }

  return errors;
}

const Wrapper = styled(Box)`
  min-width: 450px;
  min-height: 510px;
`;
